import { ClickAndCollectStore, Store, StoreInformation } from '@as-react/common-types';
import type { QueryKey, UseQueryOptions } from '@tanstack/react-query';

import type { OptionalQueryOptions, QueryApiOptions } from '../../types/types';

import {
  ClickAndCollectStoresOpeningsHoursParams,
  ClickAndCollectStoresParams,
  getClickAndCollectStores,
  getClickAndCollectStoresOpeningsHours,
  getShopLandingStoreInfo,
  getStoreInformation,
  ShopLandingStoreInfoParams,
  StoreInformationParams,
} from './store.api';

export const storeQueryKeys = {
  all: () => ['store'] as QueryKey,
  getStoreInformation: ({ businessUnitId, storeId, weeks = 1 }: StoreInformationParams) =>
    [...storeQueryKeys.all(), 'getStoreInformation', businessUnitId, storeId, weeks] as QueryKey,
  getClickAndCollectStoresOpeningsHours: (params: ClickAndCollectStoresOpeningsHoursParams) =>
    [...storeQueryKeys.all(), 'getClickAndCollectStoresOpeningsHours', params] as QueryKey,
  getClickAndCollectStores: (params: ClickAndCollectStoresParams) =>
    [...storeQueryKeys.all(), 'getClickAndCollectStores', params] as QueryKey,
  getShopLandingStoreInfo: ({ shopNumber }: ShopLandingStoreInfoParams) =>
    [...storeQueryKeys.all(), 'getShopLandingStoreInfo', shopNumber] as QueryKey,
};

export const storeQueries = (apiOptions: QueryApiOptions) => ({
  getStoreInformation: <TReturnData = StoreInformation>(
    params: StoreInformationParams,
    options: OptionalQueryOptions<StoreInformation, TReturnData>
  ): UseQueryOptions<StoreInformation, Error, TReturnData> => ({
    ...options,
    queryKey: storeQueryKeys.getStoreInformation(params),
    queryFn: () => getStoreInformation(apiOptions, params),
  }),
  getClickAndCollectStoresOpeningsHours: (
    params: ClickAndCollectStoresOpeningsHoursParams,
    options: OptionalQueryOptions<Array<ClickAndCollectStore>>
  ): UseQueryOptions<Array<ClickAndCollectStore>> => ({
    ...options,
    queryKey: storeQueryKeys.getClickAndCollectStoresOpeningsHours(params),
    queryFn: () => getClickAndCollectStoresOpeningsHours(apiOptions, params),
  }),
  getClickAndCollectStores: (
    params: ClickAndCollectStoresParams,
    options: OptionalQueryOptions<Array<ClickAndCollectStore>>
  ): UseQueryOptions<Array<ClickAndCollectStore>> => ({
    ...options,
    queryKey: storeQueryKeys.getClickAndCollectStores(params),
    queryFn: () => getClickAndCollectStores(apiOptions, params),
  }),
  getShopLandingStoreInfo: (
    params: ShopLandingStoreInfoParams,
    options: OptionalQueryOptions<Store | null>
  ): UseQueryOptions<Store | null> => ({
    ...options,
    queryKey: storeQueryKeys.getShopLandingStoreInfo(params),
    queryFn: () => getShopLandingStoreInfo(apiOptions, params),
  }),
});
